import React, { useMemo, useState } from 'react';
import { fmt } from '../data/metrics';
import { useScrollCues } from '../hooks/useScrollCues';
import { barTagEmoji, formatGuestTagsTip } from '../lib/guestTags';
import { channelLabel } from '../lib/liveBookingToasts';
import { isOverdueUnfinished } from '../lib/liveBriefing';
import {
  RIBBON_PAD_L,
  RIBBON_PAD_R,
  RIBBON_PLOT_W,
  ribbonPlotWidth,
  ribbonTimeFrac,
  ribbonXAtHour,
  ribbonXPct,
} from '../lib/ribbonTimeAxis';
import {
  formatServiceEdgeHour,
  positionShifts,
  resolveTimelineShifts,
} from '../lib/shiftOverlay';
import { msToServiceHour } from '../lib/timelineOutletFilter';
import type { ForecastData, ServiceTimelineOps, ServiceTimelineReservation } from '../types';
import '../styles/gantt-pack-seg.css';
import { Icon } from './shared/Icon';
import { OpsHoverTipPortal, useOpsHoverTip } from './shared/OpsHoverTip';
import { ShiftOverlay } from './shared/ShiftOverlay';

/** Reservation state ints aligned with backend semantics.STATES. */
const STATE_SEATED = new Set([4, 8, 10, 11]); // ARRIVED, WALK_IN, IN_PLACE, TEILEINGETROFFEN
const STATE_DONE = new Set([5, 13]); // DEPARTED, PAID
const STATE_CANCELLED = new Set([3, 6, 7, 14, 16]); // CANCELED, NOSHOW, STORNO, MOVED, CC_AUTO_DISMISSED

const STATE_SHORT: Record<string, string> = {
  seated: 'Seated',
  booked: 'Booked',
  done: 'Finished',
};

/** Cap SVG height for large floor plans (scroll for the rest). */
const MAX_LANES = 48;
/** Display meal length when GraphQL endTime is shift-close (hours). */
const DEFAULT_MEAL_H = 1.5;
const MAX_MEAL_H = 4;
/** Arrival-slot bucket for timeslot packing (hours). */
const TIMESLOT_H = 0.25;
const UNASSIGNED_TABLE = 'Unassigned';
const GANTT_PACK_KEY = 'aleno_ops_gantt_pack';

type GanttPackMode = 'none' | 'timeslot' | 'table';

const PACK_OPTIONS: Array<{ id: GanttPackMode; label: string }> = [
  { id: 'none', label: 'None' },
  { id: 'timeslot', label: 'Timeslot' },
  { id: 'table', label: 'Table' },
];

type PositionedRes = ServiceTimelineReservation & {
  startH: number;
  endH: number;
};

type LaneBar = PositionedRes & {
  lane: number;
  /** Y-axis label context: table name(s) or arrival slot clock. */
  rowLabel: string;
};

type PackResult = {
  bars: LaneBar[];
  laneCount: number;
  truncated: number;
  /** Per-lane Y label; empty string = continuation (no label). */
  laneLabels: string[];
};

function readPackMode(): GanttPackMode {
  try {
    const v = sessionStorage.getItem(GANTT_PACK_KEY);
    if (v === 'none' || v === 'table' || v === 'timeslot') return v;
  } catch {
    /* ignore */
  }
  return 'none';
}

function writePackMode(mode: GanttPackMode) {
  try {
    sessionStorage.setItem(GANTT_PACK_KEY, mode);
  } catch {
    /* ignore */
  }
}

function barEndHour(
  r: ServiceTimelineReservation,
  startH: number,
  openH: number,
  closeH: number,
): number {
  let endH = Math.max(
    startH + 0.25,
    msToServiceHour(r.endMs, openH, closeH),
  );
  const span = endH - startH;
  if (span > MAX_MEAL_H) {
    const fromDur =
      r.durationMs != null && r.durationMs > 0
        ? startH + r.durationMs / 3_600_000
        : startH + DEFAULT_MEAL_H;
    endH = Math.min(Math.max(fromDur, startH + 0.25), startH + MAX_MEAL_H);
  }
  return endH;
}

function barTone(state: number): 'seated' | 'booked' | 'done' {
  if (STATE_DONE.has(state)) return 'done';
  if (STATE_SEATED.has(state)) return 'seated';
  return 'booked';
}

/** Lane key from assigned table name(s); Unassigned when none. */
function tableLaneKey(r: ServiceTimelineReservation): string {
  const names = (r.tableNames ?? [])
    .map(n => String(n).trim())
    .filter(Boolean);
  if (names.length === 0) return UNASSIGNED_TABLE;
  return names.join(', ');
}

function tableSortRank(label: string): [number, string] {
  if (label === UNASSIGNED_TABLE) return [Number.POSITIVE_INFINITY, label];
  const m = label.match(/\d+/);
  const n = m ? parseInt(m[0], 10) : Number.POSITIVE_INFINITY - 1;
  return [n, label];
}

function shortYLab(label: string, mode: GanttPackMode): string {
  if (!label) return '';
  if (mode === 'table' && label === UNASSIGNED_TABLE) return '—';
  if (label.length <= 8) return label;
  return `${label.slice(0, 7)}…`;
}

function tableNamesTip(r: ServiceTimelineReservation): string | null {
  const names = (r.tableNames ?? [])
    .map(n => String(n).trim())
    .filter(Boolean);
  if (names.length === 0) return null;
  return `Table ${names.join(', ')}`;
}

/**
 * One Y-lane per table (or Unassigned). Bars on the same table share a row;
 * overlapping times on one table stack visually in that lane.
 */
function packTableLanes(reservations: PositionedRes[]): PackResult {
  const byTable = new Map<string, PositionedRes[]>();
  for (const r of reservations) {
    const key = tableLaneKey(r);
    const list = byTable.get(key);
    if (list) list.push(r);
    else byTable.set(key, [r]);
  }

  const sortedKeys = [...byTable.keys()].sort((a, b) => {
    const [na, sa] = tableSortRank(a);
    const [nb, sb] = tableSortRank(b);
    return na - nb || sa.localeCompare(sb, undefined, { numeric: true });
  });

  const hasUnassigned = sortedKeys.includes(UNASSIGNED_TABLE);
  const assigned = sortedKeys.filter(k => k !== UNASSIGNED_TABLE);
  let kept = sortedKeys;
  if (sortedKeys.length > MAX_LANES) {
    const capacity = Math.max(1, MAX_LANES - (hasUnassigned ? 1 : 0));
    kept = assigned.slice(0, capacity);
    if (hasUnassigned) kept.push(UNASSIGNED_TABLE);
  }

  const laneIndex = new Map(kept.map((k, i) => [k, i]));
  const bars: LaneBar[] = [];
  let truncated = 0;
  const sortedRes = [...reservations].sort(
    (a, b) =>
      a.startH - b.startH ||
      a.endH - b.endH ||
      a.id.localeCompare(b.id),
  );
  for (const r of sortedRes) {
    const rowLabel = tableLaneKey(r);
    const lane = laneIndex.get(rowLabel);
    if (lane === undefined) {
      truncated += 1;
      continue;
    }
    bars.push({ ...r, lane, rowLabel });
  }

  return {
    bars,
    laneCount: Math.max(kept.length, 1),
    truncated,
    laneLabels: kept,
  };
}

function arrivalSlotH(startH: number): number {
  return Math.floor(startH / TIMESLOT_H + 1e-9) * TIMESLOT_H;
}

/**
 * Group by 15-min arrival slot. Within a busy slot, pack non-overlapping
 * sublanes so concurrent parties don't stack on one bar.
 */
function packTimeslotLanes(reservations: PositionedRes[]): PackResult {
  const bySlot = new Map<number, PositionedRes[]>();
  for (const r of reservations) {
    const slot = arrivalSlotH(r.startH);
    const list = bySlot.get(slot);
    if (list) list.push(r);
    else bySlot.set(slot, [r]);
  }

  const slots = [...bySlot.keys()].sort((a, b) => a - b);
  const bars: LaneBar[] = [];
  const laneLabels: string[] = [];
  let truncated = 0;

  for (const slot of slots) {
    const group = [...(bySlot.get(slot) ?? [])].sort(
      (a, b) =>
        a.startH - b.startH ||
        a.endH - b.endH ||
        a.id.localeCompare(b.id),
    );
    const clock = fmtServiceClock(slot);
    const laneEnds: number[] = [];
    const pending: Array<{ r: PositionedRes; local: number }> = [];
    for (const r of group) {
      let local = laneEnds.findIndex(end => end <= r.startH + 1e-6);
      if (local < 0) {
        local = laneEnds.length;
        laneEnds.push(r.endH);
      } else {
        laneEnds[local] = r.endH;
      }
      pending.push({ r, local });
    }

    const subCount = Math.max(laneEnds.length, 1);
    if (laneLabels.length + subCount > MAX_LANES) {
      truncated += group.length;
      continue;
    }
    const base = laneLabels.length;
    for (let i = 0; i < subCount; i++) {
      laneLabels.push(i === 0 ? clock : '');
    }
    for (const { r, local } of pending) {
      bars.push({ ...r, lane: base + local, rowLabel: clock });
    }
  }

  return {
    bars,
    laneCount: Math.max(laneLabels.length, 1),
    truncated,
    laneLabels,
  };
}

/** Dense time-overlap packing — no semantic Y-axis (pre-table default). */
function packDenseLanes(reservations: PositionedRes[]): PackResult {
  const sorted = [...reservations].sort(
    (a, b) =>
      a.startH - b.startH ||
      a.endH - b.endH ||
      a.id.localeCompare(b.id),
  );
  const laneEnds: number[] = [];
  const bars: LaneBar[] = [];
  let truncated = 0;
  for (const r of sorted) {
    let lane = laneEnds.findIndex(end => end <= r.startH + 1e-6);
    if (lane < 0) {
      if (laneEnds.length >= MAX_LANES) {
        truncated += 1;
        continue;
      }
      lane = laneEnds.length;
      laneEnds.push(r.endH);
    } else {
      laneEnds[lane] = r.endH;
    }
    bars.push({ ...r, lane, rowLabel: '' });
  }
  return {
    bars,
    laneCount: Math.max(laneEnds.length, 1),
    truncated,
    laneLabels: [],
  };
}

function packLanes(reservations: PositionedRes[], mode: GanttPackMode): PackResult {
  if (mode === 'timeslot') return packTimeslotLanes(reservations);
  if (mode === 'table') return packTableLanes(reservations);
  return packDenseLanes(reservations);
}

function hourLabel(h: number): string {
  return `${((h % 24) + 24) % 24}h`;
}

function fmtServiceClock(h: number): string {
  const wrapped = ((h % 24) + 24) % 24;
  const hh = Math.floor(wrapped);
  const mm = Math.round((wrapped - hh) * 60) % 60;
  return `${hh.toString().padStart(2, '0')}:${mm.toString().padStart(2, '0')}`;
}

function serviceWindowLabel(openH: number, closeH: number): string {
  return `${formatServiceEdgeHour(openH)}–${formatServiceEdgeHour(closeH)}`;
}

export function ServiceTimelineGantt({
  forecast,
  timeline,
  showOutlet,
  onSelectReservation,
  dayLabel = 'tonight',
}: {
  forecast: ForecastData;
  timeline: ServiceTimelineOps | null;
  /** Kept for call-site compatibility; load now shown via minified Curve above. */
  nowGuests?: number;
  showOutlet: boolean;
  onSelectReservation?: (r: ServiceTimelineReservation) => void;
  /** Which service this is, so a future date does not claim to be tonight. */
  dayLabel?: string;
}) {
  const openH = forecast.openH;
  const closeH = forecast.closeH;
  const tip = useOpsHoverTip();
  const [packMode, setPackMode] = useState<GanttPackMode>(readPackMode);

  const setPackModePersist = (mode: GanttPackMode) => {
    setPackMode(mode);
    writePackMode(mode);
  };

  const positioned = useMemo(() => {
    const res = timeline?.reservations ?? [];
    return res
      .filter(r => r.peopleCount > 0 && !STATE_CANCELLED.has(r.state))
      .map(r => {
        const startH = msToServiceHour(r.startMs, openH, closeH);
        const endH = barEndHour(r, startH, openH, closeH);
        return { ...r, startH, endH };
      })
      .filter(r => r.endH > openH && r.startH < closeH);
  }, [timeline, openH, closeH]);

  const { bars, laneCount, truncated, laneLabels } = useMemo(
    () => packLanes(positioned, packMode),
    [positioned, packMode],
  );

  const nowFrac = ribbonTimeFrac(forecast.nowH, openH, closeH);
  const nowInWindow = forecast.nowH >= openH && forecast.nowH <= closeH;

  const hourTicks: number[] = [];
  for (let h = Math.ceil(openH); h <= Math.floor(closeH); h++) {
    hourTicks.push(h);
  }

  const laneH = 15;
  const padT = 4;
  const padL = RIBBON_PAD_L;
  const padR = RIBBON_PAD_R;
  const W = RIBBON_PLOT_W;
  const axisH = 28;
  const plotW = ribbonPlotWidth(padL, padR, W);
  const nowLeftPct = ribbonXPct(forecast.nowH, openH, closeH, padL, padR, W);
  const nowX = padL + nowFrac * plotW;

  const xAtH = (h: number) => ribbonXAtHour(h, openH, closeH, padL, padR, W);
  const positionedShifts = useMemo(
    () =>
      positionShifts(
        resolveTimelineShifts(timeline),
        openH,
        closeH,
        padL,
        padR,
        W,
      ),
    [timeline, openH, closeH],
  );
  // Reserve a top row for shift names so lane-0 bars don't cover them.
  const labelRowH = positionedShifts.length > 0 ? laneH : 0;
  const plotTop = padT + labelRowH;
  const plotBottom = plotTop + laneCount * laneH;
  // Bars SVG ends at the last lane — hour legend lives in a fixed footer.
  const barsH = Math.max(plotBottom + 2, 40);
  const { scrollRef, canScrollUp, canScrollDown, scrollByPage } = useScrollCues(
    `${laneCount}:${barsH}`,
  );

  const guestTotal = positioned.reduce((s, r) => s + r.peopleCount, 0);
  const windowLab = serviceWindowLabel(openH, closeH);
  const peakGuests = forecast.peak?.guests ?? forecast.peak?.covers ?? 0;
  const peakClock = fmtServiceClock(forecast.peak?.h ?? openH);

  return (
    <div className="rib-gantt">
      <div className="rib-gantt-meta">
        <div className="rib-gantt-meta-main">
          <span className="rib-gantt-stat">
            {fmt.num(positioned.length)} reservations · {fmt.num(guestTotal)} guests{' '}
            {dayLabel}
          </span>
          <span className="rib-gantt-stat-sub">
            Service window {windowLab}
            {peakGuests > 0
              ? ` · peak load ${fmt.num(peakGuests)} guests @ ${peakClock}`
              : ''}
            {truncated > 0 ? ` · +${fmt.num(truncated)} not shown` : ''}
          </span>
        </div>
        <div className="rib-gantt-meta-trail">
          <div className="rib-gantt-pack">
            <span className="rib-gantt-pack-lab" id="rib-gantt-pack-lab">
              Group by
            </span>
            <div
              className="rib-gantt-seg"
              role="group"
              aria-labelledby="rib-gantt-pack-lab"
            >
              <span
                className="rib-gantt-seg-thumb"
                style={{
                  transform: `translateX(${Math.max(
                    0,
                    PACK_OPTIONS.findIndex(o => o.id === packMode),
                  ) * 100}%)`,
                }}
                aria-hidden
              />
              {PACK_OPTIONS.map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  className={packMode === opt.id ? 'on' : ''}
                  aria-pressed={packMode === opt.id}
                  onClick={() => setPackModePersist(opt.id)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <span className="rib-gantt-legend" aria-label="Reservation status">
            <span className="rib-gantt-key seated">Seated</span>
            <span className="rib-gantt-key booked">Booked</span>
            <span className="rib-gantt-key done">Finished</span>
            <span className="rib-gantt-key vip">VIP</span>
          </span>
        </div>
      </div>

      <div className="rib-gantt-plot">
        {nowInWindow && (
          <span
            className="rib-gantt-now-flag"
            style={{ left: `${nowLeftPct}%` }}
          >
            now
          </span>
        )}
        <div className="rib-gantt-scroll-shell">
          {canScrollUp && (
            <button
              type="button"
              className="ops-scroll-cue up"
              aria-label="Scroll reservations up"
              onClick={() => scrollByPage(-1)}
            >
              <Icon name="arrowUp" size={14} stroke={2.4} />
            </button>
          )}
          {canScrollDown && (
            <button
              type="button"
              className="ops-scroll-cue down"
              aria-label="Scroll reservations down"
              onClick={() => scrollByPage(1)}
            >
              <Icon name="arrowDown" size={14} stroke={2.4} />
            </button>
          )}
        <div className="rib-gantt-scroll" ref={scrollRef}>
          <svg
            className="rib-gantt-svg"
            viewBox={`0 0 ${W} ${barsH}`}
            preserveAspectRatio="xMinYMin meet"
            role="img"
            aria-label={
              packMode === 'none'
                ? `Reservations from ${windowLab}`
                : `Reservations by ${packMode} from ${windowLab}`
            }
            style={{ aspectRatio: `${W} / ${barsH}` }}
          >
            <ShiftOverlay
              shifts={positionedShifts}
              y0={padT}
              y1={plotBottom}
              showLabels
              labelY={padT + 10}
            />
            {hourTicks.map(h => {
              const x = xAtH(h);
              return (
                <line
                  key={`grid-${h}`}
                  x1={x}
                  x2={x}
                  y1={padT}
                  y2={plotBottom}
                  className="rib-gantt-grid"
                />
              );
            })}

            {Array.from({ length: laneCount }, (_, i) => (
              <line
                key={`lane-${i}`}
                x1={padL}
                x2={W - padR}
                y1={plotTop + i * laneH + laneH}
                y2={plotTop + i * laneH + laneH}
                className="rib-gantt-lane"
              />
            ))}

            {laneLabels.map((lab, i) => {
              if (!lab) return null;
              const unassigned = packMode === 'table' && lab === UNASSIGNED_TABLE;
              return (
                <text
                  key={`ylab-${lab}-${i}`}
                  x={padL - 4}
                  y={plotTop + i * laneH + laneH / 2 + 0.5}
                  fill={unassigned ? 'var(--ink-4, var(--ink-3))' : 'var(--ink-3)'}
                  fontSize={9}
                  fontWeight={unassigned ? 500 : 650}
                  fontStyle={unassigned ? 'italic' : undefined}
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                  textAnchor="end"
                  dominantBaseline="middle"
                  pointerEvents="none"
                >
                  {shortYLab(lab, packMode)}
                </text>
              );
            })}

            {bars.map(bar => {
              const x1 = Math.max(padL, xAtH(bar.startH));
              const x2 = Math.min(W - padR, xAtH(bar.endH));
              const w = Math.max(4, x2 - x1);
              const barH = Math.max(8, laneH - 4);
              const y = plotTop + bar.lane * laneH + (laneH - barH) / 2;
              const overdue = isOverdueUnfinished(bar);
              // Past scheduled end → show as Finished even if still open in aleno.
              const tone = overdue ? 'done' : barTone(bar.state);
              const vip = !!bar.isVip;
              const hasNotes = !!(
                (bar.guestNote && bar.guestNote.trim()) ||
                (bar.staffNote && bar.staffNote.trim())
              );
              const tagEmoji = barTagEmoji(bar.guestTags);
              const tagsTip = formatGuestTagsTip(bar.guestTags);
              // Note/emoji glyphs need ~11px; skip on razor-thin bars.
              const showEmoji = !!tagEmoji && w >= 20;
              const showNote = hasNotes && w >= (showEmoji ? 34 : 22);
              const showTagMark = !showEmoji && (bar.guestTags?.length ?? 0) > 0 && w >= 18 && !showNote;
              const emojiSlot = showEmoji ? 12 : 0;
              const noteSlot = showNote || showTagMark ? 11 : 0;
              const markSlot = emojiSlot + noteSlot;
              const startClock = fmtServiceClock(bar.startH);
              const endClock = fmtServiceClock(bar.endH);
              const labelBudget = w - (vip ? 7 : 5) - markSlot;
              let label = String(bar.peopleCount);
              if (labelBudget > 72) {
                label = `${vip ? 'VIP · ' : ''}${startClock} · ${bar.peopleCount}`;
                if (showOutlet) label += ` · ${bar.outletName.slice(0, 8)}`;
              } else if (labelBudget > 42) {
                label = `${vip ? '★ ' : ''}${startClock} · ${bar.peopleCount}`;
              } else if (labelBudget > 18) {
                label = vip ? `★${bar.peopleCount}` : String(bar.peopleCount);
              } else {
                label = '';
              }
              const tipTitle = `${vip ? 'VIP · ' : ''}${bar.peopleCount} guests · ${STATE_SHORT[tone]}${
                overdue ? ' · needs attention' : ''
              }`;
              const tipText = [
                `${startClock}–${endClock}`,
                packMode === 'table'
                  ? bar.rowLabel === UNASSIGNED_TABLE
                    ? 'No table'
                    : `Table ${bar.rowLabel}`
                  : tableNamesTip(bar),
                showOutlet ? bar.outletName : null,
                channelLabel(bar.channel),
                overdue ? 'Past end — mark as departed' : null,
                tagsTip,
                hasNotes ? 'Has notes' : null,
                'Click for details',
              ]
                .filter(Boolean)
                .join(' · ');
              const contentX = x1 + (vip ? 6 : 4);
              return (
                <g
                  key={bar.id}
                  className={`rib-gantt-bar-hit${overdue ? ' overdue' : ''}`}
                  style={{ cursor: onSelectReservation ? 'pointer' : 'default' }}
                  onMouseEnter={e => tip.show(e, { title: tipTitle, text: tipText })}
                  onMouseMove={tip.move}
                  onMouseLeave={tip.hide}
                  onClick={e => {
                    e.stopPropagation();
                    tip.hide();
                    onSelectReservation?.(bar);
                  }}
                  role={onSelectReservation ? 'button' : undefined}
                  tabIndex={onSelectReservation ? 0 : undefined}
                  onKeyDown={e => {
                    if (!onSelectReservation) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelectReservation(bar);
                    }
                  }}
                >
                  <rect
                    x={x1}
                    y={y}
                    width={w}
                    height={barH}
                    rx={3}
                    className={`rib-gantt-bar ${tone}${vip ? ' vip' : ''}${overdue ? ' overdue' : ''}`}
                  />
                  {vip && (
                    <rect
                      x={x1}
                      y={y}
                      width={Math.min(3, w)}
                      height={barH}
                      rx={1}
                      className="rib-gantt-vip-mark"
                    />
                  )}
                  {showEmoji && tagEmoji && (
                    <text
                      x={contentX}
                      y={y + barH / 2 + 0.5}
                      className={`rib-gantt-bar-emoji ${tone}${vip ? ' vip' : ''}${overdue ? ' overdue' : ''}`}
                      dominantBaseline="middle"
                      aria-hidden
                    >
                      {tagEmoji}
                    </text>
                  )}
                  {showNote && (
                    <g
                      className={`rib-gantt-note-ic ${tone}${vip ? ' vip' : ''}${overdue ? ' overdue' : ''}`}
                      transform={`translate(${contentX + emojiSlot}, ${y + (barH - 9) / 2})`}
                      aria-hidden
                    >
                      {/* Compact sticky-note glyph (12×12 → scaled to ~9px). */}
                      <g transform="scale(0.75)">
                        <path
                          d="M3 1.5h5.2L11 4.3V10a1.2 1.2 0 0 1-1.2 1.2H3A1.2 1.2 0 0 1 1.8 10V2.7A1.2 1.2 0 0 1 3 1.5z"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.35"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8.2 1.5V4.2H11 M4.2 6.4h3.8 M4.2 8.4h2.6"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.35"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                    </g>
                  )}
                  {showTagMark && (
                    <g
                      className={`rib-gantt-tag-ic ${tone}${vip ? ' vip' : ''}${overdue ? ' overdue' : ''}`}
                      transform={`translate(${contentX + emojiSlot}, ${y + (barH - 9) / 2})`}
                      aria-hidden
                    >
                      {/* Compact tag glyph when notes aren't already shown. */}
                      <g transform="scale(0.75)">
                        <path
                          d="M2.2 2.4h4.6l3.8 3.8-3.4 3.4-3.8-3.8V2.4z"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.35"
                          strokeLinejoin="round"
                        />
                        <circle cx="4.2" cy="4.4" r="0.85" fill="currentColor" />
                      </g>
                    </g>
                  )}
                  {label && (
                    <text
                      x={contentX + markSlot}
                      y={y + barH / 2 + 0.5}
                      className={`rib-gantt-bar-lab ${tone}${vip ? ' vip' : ''}${overdue ? ' overdue' : ''}`}
                      dominantBaseline="middle"
                    >
                      {label}
                    </text>
                  )}
                </g>
              );
            })}

            {nowInWindow && nowX > padL && (
              <rect
                x={padL}
                y={plotTop}
                width={nowX - padL}
                height={Math.max(0, plotBottom - plotTop)}
                className="rib-gantt-past-veil"
                pointerEvents="none"
              />
            )}
            {nowInWindow && (
              <line
                x1={nowX}
                x2={nowX}
                y1={plotTop}
                y2={plotBottom}
                className="rib-gantt-now"
              />
            )}
          </svg>
        </div>
        </div>
        <div className="rib-gantt-axis" aria-hidden>
          <svg
            className="rib-gantt-axis-svg"
            viewBox={`0 0 ${W} ${axisH}`}
            preserveAspectRatio="xMinYMin meet"
            style={{ aspectRatio: `${W} / ${axisH}` }}
          >
            {hourTicks.map(h => {
              const x = xAtH(h);
              return (
                <g key={`axis-${h}`}>
                  <line
                    x1={x}
                    x2={x}
                    y1={0}
                    y2={4}
                    className="rib-gantt-axis-tick"
                  />
                  <text
                    x={x}
                    y={20}
                    fill="var(--ink-3)"
                    fontSize="11"
                    fontWeight="600"
                    textAnchor="middle"
                    dominantBaseline="auto"
                    className="rib-tick"
                  >
                    {hourLabel(h)}
                  </text>
                </g>
              );
            })}
            {nowInWindow && (
              <line
                x1={nowX}
                x2={nowX}
                y1={0}
                y2={6}
                className="rib-gantt-now"
              />
            )}
          </svg>
        </div>
      </div>
      {positioned.length === 0 && (
        <p className="rib-gantt-empty">
          No reservations {dayLabel} in the service window ({windowLab}).
        </p>
      )}
      <OpsHoverTipPortal tip={tip.tip} />
    </div>
  );
}
